import { useState, useEffect, useCallback } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../constants';
import { getDateKey, HomeworkItem, Class, LongTermAssignment, DailyChecklist } from '@homework-app/shared';
import { ApprovalDashboard } from './parent/ApprovalDashboard';
import { TemplateConfig } from './parent/TemplateConfig';

interface Connection {
  id: string;
  parent_id: string;
  student_id: string;
  status: string;
  invited_by: string;
  invite_email: string;
  student_name?: string;
  student_email?: string;
  student_grade?: number;
  created_at: string;
}

interface StudentHomework {
  homework: { [period: number]: HomeworkItem[] };
  classes: Class[];
}

// Dark theme style constants
const glass: React.CSSProperties = { background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12 };
const darkInput: React.CSSProperties = { width: '100%', padding: '0.75rem', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', color: '#f0f0f5', fontFamily: 'inherit' };
const ghostBtn: React.CSSProperties = { padding: '0.5rem 1rem', borderRadius: 8, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', color: '#8b8b9e', fontFamily: 'inherit' };
const purpleBtn: React.CSSProperties = { padding: '0.6rem 1rem', borderRadius: 8, background: '#7c3aed', color: 'white', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 };

function ParentDashboard() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [pendingInvites, setPendingInvites] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<Connection | null>(null);
  const [studentHomework, setStudentHomework] = useState<StudentHomework | null>(null);
  const [studentAssignments, setStudentAssignments] = useState<LongTermAssignment[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [activeTab, setActiveTab] = useState<'checklist' | 'homework' | 'setup'>('checklist');

  const fetchConnections = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${getApiBaseUrl()}/connections`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch connections');
      const data = await res.json();
      setConnections(data.connections || []);
      setPendingInvites(data.pendingInvites || []);
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  useEffect(() => { fetchConnections(); }, []);
  useEffect(() => { if (selectedStudent) loadStudentData(selectedStudent.student_id, currentDate); }, [currentDate, selectedStudent]);

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true); setError(''); setInviteSuccess('');
    try {
      const token = await getToken();
      const res = await fetch(`${getApiBaseUrl()}/connections/invite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: inviteEmail }),
      });
      if (!res.ok) { const data = await res.json(); throw new Error(data.error || 'Failed to send invite'); }
      setInviteSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail(''); setShowInvite(false); fetchConnections();
    } catch (err: any) { setError(err.message); } finally { setInviting(false); }
  };

  const loadStudentData = async (studentId: string, date: Date) => {
    setLoadingStudent(true);
    try {
      const token = await getToken();
      const dateKey = getDateKey(date);
      const [hwRes, assignRes] = await Promise.all([
        fetch(`${getApiBaseUrl()}/connections/${studentId}/homework/${dateKey}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${getApiBaseUrl()}/connections/${studentId}/assignments`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (hwRes.ok) setStudentHomework(await hwRes.json());
      if (assignRes.ok) setStudentAssignments(await assignRes.json());
    } catch (err: any) { setError(err.message); } finally { setLoadingStudent(false); }
  };

  const changeDate = (days: number) => { const d = new Date(currentDate); d.setDate(d.getDate() + days); setCurrentDate(d); };
  const formatDate = (date: Date) => date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const isToday = (date: Date) => date.toDateString() === new Date().toDateString();

  // Build approval API service adapter for the current student context.
  // getToken is closed over directly — no window globals needed.
  const buildApprovalService = useCallback((studentId: string) => ({
    async getChecklist(dateKey: string): Promise<DailyChecklist> {
      const res = await fetch(`${getApiBaseUrl()}/checklist/daily/${dateKey}?studentId=${studentId}`, {
        headers: { Authorization: `Bearer ${await getToken()}` }
      });
      if (!res.ok) throw new Error('Failed to fetch checklist');
      return res.json();
    },
    async updateChecklistStatus(itemId: string, status: string, comment?: string): Promise<void> {
      const res = await fetch(`${getApiBaseUrl()}/checklist/items/${itemId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getToken()}`
        },
        body: JSON.stringify({ status, parent_comment: comment })
      });
      if (!res.ok) throw new Error('Failed to update status');
    },
    async approveAll(sid: string, dateKey: string): Promise<void> {
      const res = await fetch(`${getApiBaseUrl()}/checklist/approve-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getToken()}`
        },
        body: JSON.stringify({ student_id: sid, date_key: dateKey })
      });
      if (!res.ok) throw new Error('Failed to approve all');
    },
    async getNotificationCount(): Promise<{ count: number }> {
      const res = await fetch(`${getApiBaseUrl()}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${await getToken()}` }
      });
      if (!res.ok) return { count: 0 };
      return res.json();
    }
  }), [getToken]);

  // Student detail view
  if (selectedStudent) {
    const homework = studentHomework?.homework || {};
    const classes = studentHomework?.classes || [];
    const enabledClasses = classes.filter(c => c.enabled);
    const approvalService = buildApprovalService(selectedStudent.student_id);

    const tabStyle = (active: boolean): React.CSSProperties => ({
      flex: 1,
      padding: '0.6rem',
      background: active ? 'rgba(124,58,237,0.2)' : 'transparent',
      color: active ? '#a78bfa' : '#8b8b9e',
      border: 'none',
      borderBottom: `2px solid ${active ? '#7c3aed' : 'transparent'}`,
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontWeight: active ? 600 : 400,
      fontSize: '0.9rem',
      transition: 'all 0.15s ease'
    });

    return (
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '1rem', minHeight: '100vh', background: '#0a0a0f' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <button onClick={() => { setSelectedStudent(null); setStudentHomework(null); setActiveTab('checklist'); }} style={ghostBtn}>← Back</button>
          <h1 style={{ margin: 0, fontSize: '1.3rem', color: '#f0f0f5' }}>{selectedStudent.student_name}</h1>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', marginBottom: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <button style={tabStyle(activeTab === 'checklist')} onClick={() => setActiveTab('checklist')}>
            Checklist
          </button>
          <button style={tabStyle(activeTab === 'homework')} onClick={() => setActiveTab('homework')}>
            Homework
          </button>
          <button style={tabStyle(activeTab === 'setup')} onClick={() => setActiveTab('setup')}>
            Setup
          </button>
        </div>

        {/* Checklist / approval tab */}
        {activeTab === 'checklist' && (
          <ApprovalDashboard
            studentId={selectedStudent.student_id}
            studentName={selectedStudent.student_name || 'Student'}
            apiService={approvalService}
          />
        )}

        {/* Setup tab — template configuration */}
        {activeTab === 'setup' && (
          <TemplateConfig
            studentId={selectedStudent.student_id}
            apiService={{
              getTemplates: async () => {
                const res = await fetch(`${getApiBaseUrl()}/checklist/templates`, {
                  headers: { Authorization: `Bearer ${await getToken()}` }
                });
                if (!res.ok) throw new Error('Failed to fetch templates');
                return res.json();
              },
              createTemplate: async (data) => {
                const res = await fetch(`${getApiBaseUrl()}/checklist/templates`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${await getToken()}`
                  },
                  body: JSON.stringify(data)
                });
                if (!res.ok) throw new Error('Failed to create template');
                return res.json();
              },
              updateTemplate: async (id, data) => {
                const res = await fetch(`${getApiBaseUrl()}/checklist/templates/${id}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${await getToken()}`
                  },
                  body: JSON.stringify(data)
                });
                if (!res.ok) throw new Error('Failed to update template');
                return res.json();
              },
              deleteTemplate: async (id) => {
                const res = await fetch(`${getApiBaseUrl()}/checklist/templates/${id}`, {
                  method: 'DELETE',
                  headers: { Authorization: `Bearer ${await getToken()}` }
                });
                if (!res.ok) throw new Error('Failed to delete template');
              },
              seedTemplates: async (studentId) => {
                const res = await fetch(`${getApiBaseUrl()}/checklist/templates/seed`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${await getToken()}`
                  },
                  body: JSON.stringify({ student_id: studentId })
                });
                if (!res.ok) throw new Error('Failed to seed templates');
                return res.json();
              }
            }}
          />
        )}

        {/* Homework tab */}
        {activeTab === 'homework' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <button onClick={() => changeDate(-1)} style={ghostBtn}>←</button>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#f0f0f5' }}>{formatDate(currentDate)}</div>
                {!isToday(currentDate) && (
                  <button onClick={() => setCurrentDate(new Date())} style={{ fontSize: '0.8rem', color: '#a78bfa', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Today</button>
                )}
              </div>
              <button onClick={() => changeDate(1)} style={ghostBtn}>→</button>
            </div>

            {loadingStudent && <p style={{ textAlign: 'center', color: '#8b8b9e' }}>Loading...</p>}

            {enabledClasses.map(cls => {
              const items = homework[cls.period] || [];
              return (
                <div key={cls.period} style={{ marginBottom: '1rem', ...glass, padding: '1rem' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.5rem', color: '#f0f0f5' }}>
                    P{cls.period} — {cls.subject}
                    <span style={{ fontWeight: 400, color: '#5a5a6e', marginLeft: '0.5rem' }}>{cls.teacher}</span>
                  </div>
                  {items.length === 0 ? (
                    <p style={{ color: '#5a5a6e', fontSize: '0.85rem', margin: 0 }}>No homework</p>
                  ) : (
                    items.map(item => (
                      <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.35rem 0' }}>
                        <span style={{ fontSize: '1.1rem' }}>{item.completed ? '✅' : '⬜'}</span>
                        <span style={{ color: item.completed ? '#5a5a6e' : '#f0f0f5', textDecoration: item.completed ? 'line-through' : 'none' }}>
                          {item.text || '(empty)'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              );
            })}

            {studentAssignments.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#f0f0f5' }}>Long-Term Assignments</h2>
                {studentAssignments.map(a => (
                  <div key={a.id} style={{ ...glass, padding: '1rem', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 600, color: '#f0f0f5' }}>{a.title}</span>
                        {a.subject && <span style={{ color: '#8b8b9e', marginLeft: '0.5rem', fontSize: '0.85rem' }}>{a.subject}</span>}
                      </div>
                      <span style={{ fontSize: '0.85rem', color: a.completed ? '#22c55e' : '#f59e0b' }}>
                        {a.completed ? 'Done' : `Due ${a.due_date}`}
                      </span>
                    </div>
                    {a.milestones && a.milestones.length > 0 && (
                      <div style={{ marginTop: '0.5rem', paddingLeft: '0.5rem' }}>
                        {a.milestones.map((m: any) => (
                          <div key={m.id} style={{ fontSize: '0.85rem', color: m.completed ? '#5a5a6e' : '#f0f0f5' }}>
                            {m.completed ? '[x]' : '[ ]'} {m.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Main parent dashboard
  const confirmedConnections = connections.filter(c => c.status === 'confirmed');
  const pendingConnections = connections.filter(c => c.status === 'pending');

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '1.5rem 1rem', minHeight: '100vh', background: '#0a0a0f' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', background: 'linear-gradient(135deg, #a78bfa, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>👨‍👩‍👧 Parent Dashboard</h1>
        <button onClick={() => signOut()} style={ghostBtn}>Sign Out</button>
      </div>

      {error && (
        <div className="banner banner--error" style={{ marginBottom: '1rem' }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#fca5a5' }}>×</button>
        </div>
      )}

      {inviteSuccess && (
        <div style={{ background: 'rgba(34,197,94,0.15)', color: '#86efac', padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem', border: '1px solid rgba(34,197,94,0.3)' }}>
          {inviteSuccess}
        </div>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', color: '#8b8b9e' }}>Loading...</p>
      ) : (
        <>
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#f0f0f5' }}>Connected Students</h2>
            {confirmedConnections.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', ...glass }}>
                <p style={{ color: '#8b8b9e', marginBottom: '1rem' }}>No connected students yet.</p>
                <p style={{ color: '#5a5a6e', fontSize: '0.85rem' }}>Invite a student to get started!</p>
              </div>
            ) : (
              confirmedConnections.map(conn => (
                <button
                  key={conn.id}
                  onClick={() => { setSelectedStudent(conn); setCurrentDate(new Date()); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '1rem',
                    padding: '1rem', ...glass, cursor: 'pointer', marginBottom: '0.75rem', textAlign: 'left',
                    fontFamily: 'inherit', transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(124,58,237,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>
                    🎒
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#f0f0f5' }}>{conn.student_name || 'Student'}</div>
                    <div style={{ fontSize: '0.85rem', color: '#8b8b9e' }}>
                      {conn.student_email}
                      {conn.student_grade && ` • Grade ${conn.student_grade}`}
                    </div>
                  </div>
                  <span style={{ color: '#5a5a6e' }}>→</span>
                </button>
              ))
            )}
          </div>

          {pendingConnections.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#f0f0f5' }}>Pending Invitations</h2>
              {pendingConnections.map(conn => (
                <div key={conn.id} style={{ padding: '1rem', background: 'rgba(245,158,11,0.08)', borderRadius: 12, border: '1px solid rgba(245,158,11,0.2)', marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: 500, color: '#f0f0f5' }}>
                    {conn.invited_by === 'parent' ? `Invited ${conn.invite_email}` : `From ${conn.student_name || conn.invite_email}`}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#f59e0b', marginTop: '0.25rem' }}>Waiting for confirmation</div>
                </div>
              ))}
            </div>
          )}

          {pendingInvites.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#f0f0f5' }}>Incoming Invitations</h2>
              {pendingInvites.map(conn => (
                <div key={conn.id} style={{ padding: '1rem', background: 'rgba(124,58,237,0.08)', borderRadius: 12, border: '1px solid rgba(124,58,237,0.2)', marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: 500, color: '#f0f0f5' }}>{conn.student_name || 'A student'} wants to connect</div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <button
                      onClick={async () => {
                        const token = await getToken();
                        await fetch(`${getApiBaseUrl()}/connections/${conn.id}/confirm`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
                        fetchConnections();
                      }}
                      style={purpleBtn}
                    >Accept</button>
                    <button
                      onClick={async () => {
                        const token = await getToken();
                        await fetch(`${getApiBaseUrl()}/connections/${conn.id}/reject`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
                        fetchConnections();
                      }}
                      style={ghostBtn}
                    >Decline</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!showInvite ? (
            <button
              onClick={() => setShowInvite(true)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: 8, background: 'linear-gradient(135deg, #7c3aed, #f59e0b)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '1rem', fontFamily: 'inherit', fontWeight: 600 }}
            >+ Connect to a Student</button>
          ) : (
            <div style={{ ...glass, padding: '1.25rem' }}>
              <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: '#f0f0f5' }}>Connect to a Student</h3>
              <p style={{ fontSize: '0.85rem', color: '#8b8b9e', marginBottom: '1rem' }}>Enter the student's email address.</p>
              <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="student@email.com" style={{ ...darkInput, marginBottom: '0.75rem' }} autoFocus />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => { setShowInvite(false); setInviteEmail(''); }} style={{ ...ghostBtn, flex: 1 }}>Cancel</button>
                <button onClick={sendInvite} disabled={!inviteEmail.trim() || inviting} style={{ ...purpleBtn, flex: 2, opacity: !inviteEmail.trim() || inviting ? 0.5 : 1 }}>
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ParentDashboard;
